import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { supabase } from "../services/dataService";

function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const usernameRef = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        const user = JSON.parse(localStorage.getItem("user") || "null");
        navigate(user?.role === "admin" ? "/admin" : "/cashier", { replace: true });
      }
    });
    setTimeout(() => usernameRef.current?.focus(), 100);
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!username.trim()) {
      setError("Please enter your username");
      usernameRef.current?.focus();
      return;
    }
    if (!password.trim()) {
      setError("Please enter your password");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/auth/login", {
        username: username.trim(),
        password: password.trim(),
      });

      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));

      const role = response.data.user.role;
      navigate(role === "admin" ? "/admin" : "/cashier", { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || "Login failed. Please check your credentials.");
      setPassword("");
      usernameRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-shell">

        {/* ── Left brand panel ── */}
        <aside className="login-brand-panel">
          <div className="login-brand-logo">
            <img src="/images/zawkhinLogo.PNG" alt="Zaw Khin Grocery & Import Shop" />
            <span className="login-brand-logo-name">Zaw Khin Market</span>
          </div>

          <div className="login-brand-badge">Grocery Store POS</div>
          <h1 className="login-brand-title">
            Fast checkout.<br />Every shift.
          </h1>
          <p className="login-brand-copy">
            Built for quick barcode entry, clear totals, and fast checkout
            without the clutter.
          </p>

          <div className="login-brand-features">
            <div className="login-brand-feature">
              <span className="login-brand-feature-dot" />
              Instant barcode scanning
            </div>
            <div className="login-brand-feature">
              <span className="login-brand-feature-dot" />
              Role-based access control
            </div>
            <div className="login-brand-feature">
              <span className="login-brand-feature-dot" />
              Real-time inventory sync
            </div>
            <div className="login-brand-feature">
              <span className="login-brand-feature-dot" />
              Printable receipts
            </div>
          </div>
        </aside>

        {/* ── Right form panel ── */}
        <section className="login-form-panel">
          <div className="login-form-inner">

            <div className="login-form-logo">
              <img src="/images/zawkhinLogo.PNG" alt="Zaw Khin Grocery & Import Shop" />
              <span className="login-form-logo-name">Zaw Khin Market</span>
            </div>

            <div className="login-form-header">
              <div className="login-form-kicker">Sign in</div>
              <h2>Welcome back</h2>
              <p>Enter your credentials. You'll be directed to the right screen based on your role.</p>
            </div>

            {error && <div className="alert alert-error login-alert" role="alert">{error}</div>}

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="login-username">Username</label>
                <input
                  id="login-username"
                  ref={usernameRef}
                  type="text"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setError(""); }}
                  required
                  autoComplete="username"
                  placeholder="Your username"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="login-password">Password</label>
                <div className="login-password-row">
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                    required
                    autoComplete="current-password"
                    placeholder="Password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="login-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary login-submit-btn"
                disabled={loading || !username.trim() || !password.trim()}
              >
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>

            <p className="login-footer-note">
              Zaw Khin Market POS &mdash; All rights reserved
            </p>
          </div>
        </section>

      </div>
    </div>
  );
}

export default Login;
